import React, { useEffect, useState } from 'react';
import { FinancialSummary } from '../components/player/FinancialSummary';
import { Assets } from '../components/player/Assets';
import { Liabilities } from '../components/player/Liabilities';
import { Contracts } from '../components/player/Contracts';
import { Income } from '../components/player/Income';
import { Expenses } from '../components/player/Expenses';
import { Edit2, Check, Save } from 'lucide-react';
export function Player5() {
  const [playerName, setPlayerName] = useState('Player 5');
  const [isEditing, setIsEditing] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Financial data state
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalLiabilities, setTotalLiabilities] = useState(0);
  const [totalAssetCashflow, setTotalAssetCashflow] = useState(0);
  const [totalLiabilityPayments, setTotalLiabilityPayments] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalCash, setTotalCash] = useState(5000);
  // Load data from localStorage on initial render
  useEffect(() => {
    const savedData = localStorage.getItem('player5Data');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setPlayerName(parsedData.playerName || 'Player 5');
        setTotalAssets(parsedData.totalAssets || 0);
        setTotalLiabilities(parsedData.totalLiabilities || 0);
        setTotalAssetCashflow(parsedData.totalAssetCashflow || 0);
        setTotalLiabilityPayments(parsedData.totalLiabilityPayments || 0);
        setTotalIncome(parsedData.totalIncome || 0);
        setTotalExpenses(parsedData.totalExpenses || 0);
        setTotalCash(parsedData.totalCash || 5000);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerName(e.target.value);
  };
  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
    }
  };
  const handleAssetChange = (assetValue: number, cashflow: number) => {
    setTotalAssets(assetValue);
    setTotalAssetCashflow(cashflow);
  };
  const handleLiabilitiesChange = (liabilityValue: number) => {
    setTotalLiabilities(liabilityValue);
  };
  const handleLiabilityPaymentsChange = (payments: number) => {
    setTotalLiabilityPayments(payments);
  };
  const handleIncomeChange = (income: number) => {
    setTotalIncome(income);
  };
  const handleExpensesChange = (expenses: number) => {
    setTotalExpenses(expenses);
  };
  const handleCashChange = (cash: number) => {
    setTotalCash(cash);
  };
  const handleSave = () => {
    const dataToSave = {
      playerName,
      totalAssets,
      totalLiabilities,
      totalAssetCashflow,
      totalLiabilityPayments,
      totalIncome,
      totalExpenses,
      totalCash,
      lastSaved: new Date().toISOString()
    };
    localStorage.setItem('player5Data', JSON.stringify(dataToSave));
    // Show save confirmation message
    setSaveMessage('Data saved successfully!');
    // Clear the message after 3 seconds
    setTimeout(() => {
      setSaveMessage('');
    }, 3000);
    // Dispatch custom event to notify other components of the update
    // Using CustomEvent instead of Event for better compatibility
    const updateEvent = new CustomEvent('player5Updated', {
      bubbles: true,
      detail: {
        timestamp: new Date().getTime()
      }
    });
    window.dispatchEvent(updateEvent);
    // Also trigger a storage event manually for same-tab updates
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'player5Data',
      newValue: JSON.stringify(dataToSave),
      storageArea: localStorage
    }));
  };
  return <div className="p-8 max-w-6xl mx-auto" data-id="element-348">
      <div className="flex items-center justify-between mb-6" data-id="element-349">
        <div className="flex items-center" data-id="element-350">
          {isEditing ? <div className="flex items-center" data-id="element-351">
              <input type="text" value={playerName} onChange={handleNameChange} onKeyDown={handleKeyDown} className="text-3xl font-bold text-gray-800 border-b-2 border-green-500 mr-2 focus:outline-none" autoFocus data-id="element-352" />
              <button onClick={toggleEdit} className="text-green-500" data-id="element-353">
                <Check size={20} data-id="element-354" />
              </button>
            </div> : <div className="flex items-center" data-id="element-355">
              <h1 className="text-3xl font-bold text-gray-800 mr-2" data-id="element-356">
                {playerName}
              </h1>
              <button onClick={toggleEdit} className="text-gray-500 hover:text-green-500" data-id="element-357">
                <Edit2 size={16} data-id="element-358" />
              </button>
            </div>}
        </div>
        <div className="flex items-center" data-id="element-359">
          {saveMessage && <span className="text-green-600 mr-3 font-medium" data-id="element-360">
              {saveMessage}
            </span>}
          <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors" data-id="element-361">
            <Save size={18} data-id="element-362" />
            Save
          </button>
        </div>
      </div>
      <div className="space-y-6" data-id="element-363">
        {/* Financial Summary - 100% width */}
        <FinancialSummary totalAssets={totalAssets} totalLiabilities={totalLiabilities} monthlyIncome={totalIncome} monthlyExpenses={totalExpenses} initialCash={totalCash} onCashChange={handleCashChange} data-id="element-364" />
        {/* Assets and Liabilities - 50% width each */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-365">
          <Assets onCashflowChange={cashflow => {}} onValueChange={(value, cashflow) => handleAssetChange(value, cashflow)} data-id="element-366" />
          <Liabilities onValueChange={handleLiabilitiesChange} onPaymentsChange={handleLiabilityPaymentsChange} data-id="element-367" />
        </div>
        {/* Contracts - 100% width */}
        <Contracts data-id="element-368" />
        {/* Income and Expenses - 50% width each */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-369">
          <Income totalAssetCashflow={totalAssetCashflow} onIncomeChange={handleIncomeChange} data-id="element-370" />
          <Expenses liabilityPayments={totalLiabilityPayments} onExpensesChange={handleExpensesChange} data-id="element-371" />
        </div>
      </div>
    </div>;
}